"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveStyles = exports.cardStyle = exports.condensedStyle = exports.defaultStyle = exports.expandableHeaderRowHeight = exports.totalsRowHeight = exports.getTableStyles = void 0;
const Css_1 = require("../../Css");
const utils_1 = require("../../utils");
// Returns a "blessed" style of GridTable
function memoizedTableStyles() {
    const cache = {};
    return (props = {}) => {
        const { inlineEditing = false, grouped = false, rowHeight = "flexible", cellHighlight = false, allWhite = false, bordered = false, vAlign = "center", cellTypography = "xs", } = props;
        const key = (0, utils_1.safeKeys)(props)
            .sort()
            .map((k) => `${k}_${props[k]}`)
            .join("|");
        if (!cache[key]) {
            const alignItems = vAlign === "center" ? "center" : vAlign === "top" ? "flex-start" : "flex-end";
            const groupedLevels = {
                0: {
                    cellCss: {
                        ...Css_1.Css.xsMd.mhPx(56).gray700.bgGray100.boxShadow(`inset 0 -1px 0 ${Css_1.Palette.Gray200}`).$,
                        ...(allWhite && Css_1.Css.bgWhite.$),
                    },
                    firstContentColumn: { ...Css_1.Css.smMd.$, ...(allWhite && Css_1.Css.smBd.gray900.$) },
                },
                2: { firstContentColumn: Css_1.Css.tiny.pl3.$ },
                // Add 12 more pixels of padding for each level of nesting
                3: { firstContentColumn: Css_1.Css.tiny.plPx(36).$ },
            };
            const defaultLevels = { 1: { firstContentColumn: Css_1.Css.tiny.pl3.$ } };
            cache[key] = {
                emptyCell: "-",
                firstRowMessageCss: {
                    ...Css_1.Css.tac.py3.$,
                    ...(allWhite && Css_1.Css.bgWhite.$),
                    ...(bordered && Css_1.Css.bl.br.bcGray200.$),
                },
                headerCellCss: {
                    // We want to support headers having two lines of wrapped text, and could add a `lineClamp2` here, but
                    // lineClamp requires `display: webkit-box`, which disables `align-items: center` (requires `display: flex/grid`)
                    // Header's will add `lineClamp2` more locally in their renders.
                    // Also `unset`-ing the white-space: nowrap defined in `cellCss` below.
                    ...Css_1.Css.gray700.xsMd.bgGray200.aic.pxPx(12).whiteSpace("unset").hPx(40).$,
                    ...(allWhite && Css_1.Css.bgWhite.$),
                },
                totalsCellCss: Css_1.Css.bgWhite.gray700.bgGray100.xsMd.hPx(exports.totalsRowHeight).pPx(12).$,
                expandableHeaderCss: Css_1.Css.bgWhite.gray900.xsMd.wsn
                    .hPx(exports.expandableHeaderRowHeight)
                    .pxPx(12)
                    .py0.boxShadow(`inset 0 -1px 0 ${Css_1.Palette.Gray200}`)
                    .addIn("&:not(:last-of-type)", Css_1.Css.boxShadow(`inset -1px -1px 0 ${Css_1.Palette.Gray200}`).$).$,
                cellCss: {
                    ...Css_1.Css[cellTypography].gray900.bgWhite.ai(alignItems).pxPx(12).boxShadow(`inset 0 -1px 0 ${Css_1.Palette.Gray200}`)
                        .$,
                    ...(rowHeight === "flexible" ? Css_1.Css.pyPx(12).$ : Css_1.Css.wsnw.hPx(inlineEditing ? 48 : 36).$),
                    ...(cellHighlight ? { "&:hover": Css_1.Css.bgGray100.$ } : {}),
                    ...(bordered && { "&:first-child": Css_1.Css.bl.bcGray200.$, "&:last-child": Css_1.Css.br.bcGray200.$ }),
                },
                firstRowCss: {
                    ...Css_1.Css.addIn("& > *:first-of-type", Css_1.Css.borderRadius("8px 0 0 0 ").$).addIn("& > *:last-of-type", Css_1.Css.borderRadius("0 8px 0 0").$).$,
                    ...(bordered && Css_1.Css.addIn("& > *", Css_1.Css.bt.bcGray200.$).$),
                },
                // Only apply border radius styles to the last row when using the `bordered` style table.
                lastRowCss: bordered
                    ? Css_1.Css.addIn("& > *:first-of-type", Css_1.Css.borderRadius("0 0 0 8px").$).addIn("& > *:last-of-type", Css_1.Css.borderRadius("0 0 8px 0").$).$
                    : Css_1.Css.addIn("> *", Css_1.Css.bsh0.$).$,
                presentationSettings: { borderless: true, typeScale: "xs", wrap: rowHeight === "flexible" },
                levels: grouped ? groupedLevels : defaultLevels,
                rowHoverColor: Css_1.Palette.Blue100,
                keptGroupRowCss: Css_1.Css.bgYellow100.gray900.xsMd.df.aic.$,
                keptLastRowCss: Css_1.Css.boxShadow("inset 0px -14px 8px -11px rgba(63,63,63,.18)").$,
            };
        }
        return cache[key];
    };
}
exports.getTableStyles = memoizedTableStyles();
exports.totalsRowHeight = 40;
exports.expandableHeaderRowHeight = 40;
/** Our original table look & feel/style. */
exports.defaultStyle = (0, exports.getTableStyles)({});
/** Tightens up the padding of rows, great for rows that have form elements in them. */
exports.condensedStyle = {
    ...(0, exports.getTableStyles)({ rowHeight: "fixed" }),
    firstRowMessageCss: {
        ...(0, exports.getTableStyles)({ rowHeight: "fixed" }).firstRowMessageCss,
        ...Css_1.Css.xs.gray900.$,
    },
};
/** Renders each row as a card.
 * TODO: Add `cardStyle` option to `getTableStyles` and remove this.
 * */
exports.cardStyle = {
    ...exports.defaultStyle,
    betweenRowsCss: {},
    nonHeaderRowCss: Css_1.Css.br4.oh.ba.bcGray400.mt2.add("transition", "all 240ms").$,
    firstRowCss: Css_1.Css.bl.br.bcGray200.borderRadius("8px 8px 0 0").oh.$,
    cellCss: Css_1.Css.p2.$,
    // Undo the card look & feel for the header
    headerCellCss: {
        ...exports.defaultStyle.headerCellCss,
        ...Css_1.Css.p1.m0.xsMd.gray700.$,
    },
    rowHoverColor: "none",
    nonHeaderRowHoverCss: Css_1.Css.bshHover.bcGray700.$,
    levels: {
        1: { rowIndent: 24 },
        2: { rowIndent: 48 },
    },
};
function resolveStyles(style) {
    const defKeysRecord = {
        inlineEditing: true,
        grouped: true,
        rowHeight: true,
        cellHighlight: true,
        allWhite: true,
        bordered: true,
        rowHover: true,
        vAlign: true,
        cellTypography: true,
    };
    const keys = (0, utils_1.safeKeys)(style);
    const defKeys = (0, utils_1.safeKeys)(defKeysRecord);
    if (keys.length === 0 || keys.some((k) => defKeys.includes(k))) {
        return (0, exports.getTableStyles)(style);
    }
    return style;
}
exports.resolveStyles = resolveStyles;
